import type { ManagerProfile, RivalSummary } from "@averagejoesff/shared";
import { AppLink } from "../../components/AppLink";
import { formatOrdinal, formatPoints, formatRecord } from "../../lib/format";
import { ManagerAvatar } from "./ManagerAvatar";
import type { PeopleLookup } from "./people";
import { ManagerLink, personName } from "./people";

interface ProfileViewProps {
  profile: ManagerProfile;
  people: PeopleLookup;
  isMe: boolean;
  onSetMe: () => void;
}

const record = (r: { wins: number; losses: number; ties: number }) => formatRecord(r.wins, r.losses, r.ties);
/** "+4%" / "-3%" — how far the actual win rate sits from the all-play one. */
const luckLabel = (luck: number) => `${luck > 0 ? "+" : luck < 0 ? "−" : ""}${Math.abs(Math.round(luck * 100))}%`;

/** One manager's career: the numbers, season by season, and who they own or fear. */
export function ProfileView({ profile, people, isMe, onSetMe }: ProfileViewProps) {
  const { luck, transactions } = profile;
  return (
    <article className="profile" aria-labelledby="profile-name">
      <header className="profile__header">
        <div className="profile__identity">
          <ManagerAvatar name={profile.name} logoUrl={profile.logoUrl} />
          <h3 id="profile-name" className="profile__name">
            {profile.name}
            {isMe && <span className="tag tag--you">You</span>}
            {!profile.active && <span className="tag">Former</span>}
          </h3>
        </div>
        {profile.titles.length > 0 && (
          <p className="profile__titles">
            🏆 {profile.titles.length === 1 ? "Champion" : `${profile.titles.length}-time champion`} · {profile.titles.join(", ")}
          </p>
        )}
        {!isMe && (
          <button type="button" className="profile__set-me" onClick={onSetMe}>
            This is me
          </button>
        )}
      </header>

      <dl className="payout-stats profile__stats">
        <div className="payout-stats__item">
          <dt>Regular season</dt>
          <dd>{record(profile.regularSeason)}</dd>
        </div>
        <div className="payout-stats__item">
          <dt>Playoffs</dt>
          <dd>{record(profile.playoffs)}</dd>
        </div>
        <div className="payout-stats__item">
          <dt>Average points per game</dt>
          <dd>{formatPoints(profile.pointsPerGame)}</dd>
        </div>
        <div className="payout-stats__item">
          <dt>Playoff trips</dt>
          <dd>{profile.playoffAppearances}</dd>
        </div>
      </dl>

      <h4 className="feature__subhead">Seasons</h4>
      <div className="table-scroll">
        <table className="standings">
          <caption className="visually-hidden">{profile.name} season by season</caption>
          <thead>
            <tr>
              <th scope="col">Season</th>
              <th scope="col" className="standings__num">
                Record
              </th>
              <th scope="col" className="standings__num">
                PF
              </th>
              <th scope="col" className="standings__num">
                Finish
              </th>
            </tr>
          </thead>
          <tbody>
            {[...profile.seasons].reverse().map((line) => (
              <tr key={line.season} className="standings__row">
                <td className="standings__team">
                  <span className="standings__season">{line.season}</span>
                  <span className="standings__team-name">{line.teamName}</span>
                </td>
                <td className="standings__num">{record(line.record)}</td>
                <td className="standings__num">{formatPoints(line.pointsFor)}</td>
                <td className="standings__num">
                  {line.finish === null ? "—" : line.champion ? "🏆 1st" : formatOrdinal(line.finish)}
                  {!line.complete && <span className="visually-hidden"> (in progress)</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4 className="feature__subhead">Highlights</h4>
      <ul className="home__lines">
        {profile.highScore && <li>📈 Highest score: {formatPoints(profile.highScore.points)} ({gameWhen(profile.highScore, people)})</li>}
        {profile.lowScore && <li>📉 Lowest score: {formatPoints(profile.lowScore.points)} ({gameWhen(profile.lowScore, people)})</li>}
        {profile.longestWinStreak && <li>🔥 Longest win streak: {profile.longestWinStreak.length} ({streakWhen(profile.longestWinStreak)})</li>}
        {profile.longestLossStreak && <li>🧊 Longest losing streak: {profile.longestLossStreak.length} ({streakWhen(profile.longestLossStreak)})</li>}
        {profile.bestSeason && profile.worstSeason && profile.bestSeason !== profile.worstSeason && (
          <li>
            Best season: {profile.bestSeason.season} ({record(profile.bestSeason.record)}). Worst: {profile.worstSeason.season} (
            {record(profile.worstSeason.record)}).
          </li>
        )}
        {luck && Math.abs(luck.luck) >= 0.03 && (
          <li>
            {luck.luck > 0 ? "🍀" : "🎲"} Luck: win rate is {luckLabel(luck.luck)} {luck.luck > 0 ? "above" : "below"} what their scores earn
            against the whole league each week.
          </li>
        )}
      </ul>

      {(profile.nemesis || profile.favoriteVictim) && (
        <>
          <h4 className="feature__subhead">Head-to-head</h4>
          <ul className="home__lines">
            {profile.favoriteVictim && <Rival label="Favorite victim" rival={profile.favoriteVictim} people={people} />}
            {profile.nemesis && <Rival label="Nemesis" rival={profile.nemesis} people={people} />}
          </ul>
          <p className="home__line">
            <AppLink href="/matchups">Look up any pairing on Matchups</AppLink>
          </p>
        </>
      )}

      {/* Only where Yahoo reported them; the oldest seasons did not. */}
      {transactions.seasonsTracked > 0 && (
        <p className="home__line">
          {transactions.moves} adds and drops and {transactions.trades} {transactions.trades === 1 ? "trade" : "trades"} across{" "}
          {transactions.seasonsTracked} {transactions.seasonsTracked === 1 ? "season" : "seasons"}.
        </p>
      )}
    </article>
  );
}

function Rival({ label, rival, people }: { label: string; rival: RivalSummary; people: PeopleLookup }) {
  return (
    <li>
      {label}: <ManagerLink personId={rival.opponentId} people={people} /> ({record(rival.record)})
    </li>
  );
}

const gameWhen = (g: { season: number; week: number; gameType: string; opponentId: string }, people: PeopleLookup) =>
  `${g.season} week ${g.week}${g.gameType === "playoffs" ? ", playoffs" : ""}, against ${personName(people, g.opponentId)}`;

const streakWhen = (s: { season: number; startWeek: number; endWeek: number }) =>
  s.startWeek === s.endWeek ? `${s.season}, week ${s.startWeek}` : `${s.season}, weeks ${s.startWeek}–${s.endWeek}`;
